import { Icon, type IContextualMenuItem } from '@fluentui/react';
import {
  AppName,
  type AppUser,
  convertNavLinksToMenuItems,
  type FeatureStoreMenuInfo,
  Image,
  navigate,
  type NavigationElementConfig,
  type NavigationSectionConfig,
} from '@h2oai/ui-kit';
import type { History } from 'history';

import type { User } from '../../ai.h2o.cloud.appstore';
import type { Permissions } from '../../authz/providers/RoleProvider';
import { AppStoreMenuName } from '../../discovery-service/constants';
import type { CloudPlatformDiscoveryConfig, EnvironmentAndMenu } from '../../utils/contexts';
import { NavigationIcon } from './components/NavigationIcon';
import { ReactComponent as DocAIIcon } from './icons/DocumentAI.svg';
import { ReactComponent as EnterpriseIcon } from './icons/Enterprise.svg';
import { ReactComponent as EvalIcon } from './icons/Eval.svg';
import { ReactComponent as MLOpsIcon } from './icons/MLOps.svg';
import { ReactComponent as NotebookIcon } from './icons/Notebook.svg';
import { ReactComponent as SteamIcon } from './icons/Steam.svg';
import {
  ADMIN_NAV_ITEMS,
  filterNavItems,
  MAIN_NAV_ITEMS,
  MY_NAV_ITEMS,
  type NavContext,
  OPERATIONS_NAV_ITEMS,
} from './navigationItems';
import type { AppStoreMenuInfo, MapMenuItem, NavigationConfig } from './types';

type MapMenuItemExtended<T> = MapMenuItem<
  T & {
    rendered?: boolean;
  }
>;

const navArrayToObject = <T extends { key: string }>(arr: T[] = []): MapMenuItemExtended<T> => {
  return arr.reduce<MapMenuItemExtended<T>>((acc, item) => {
    acc[item.key.replace(/ /g, '')] = item;

    return acc;
  }, {} as MapMenuItemExtended<T>);
};

export const formatDataTestId = (testId = '') => testId.replace(/ /g, '-').replace(/_/g, '-').toLowerCase();

const getMenuElementsIfExist = (menuObject: MapMenuItem<any>, keys: string[], markRendered = false) => {
  const elements: any[] = [];

  keys.forEach((key: string) => {
    const linkObjectReference = menuObject[key];
    if (linkObjectReference) {
      // Mutates the provided object
      if (markRendered) linkObjectReference.rendered = true;

      const element = {
        ...linkObjectReference,
        dataTest: formatDataTestId(linkObjectReference.dataTest)
          .replace('--menu-item', '--main-menu-item')
          .replace('--user-menu-item', '--main-menu-item'),
      };
      elements.push(element);
    }
  });

  return elements;
};

export const getAppStoreMenuInfo = (
  hasAiEngineManager?: boolean,
  user?: AppUser,
  navLinks: { label: string; url: string }[] = [],
  bookTimeLink = 'https://h2o.ai',
  hasHomePage = false,
  federationEnabled = false,
  url?: string,
  hasMenu?: boolean,
  appName: AppName = AppName.appStore,
  environmentName?: string,
  hasPlatformUsage?: boolean,
  isPublicMode = false,
  hasNotebook = false,
  hasSecureStore = false,
  cliDownloadEnabled = false,
  adminCenterEnabled = false,
  hasAuditTrail = false,
  driveEnabled = false
): AppStoreMenuInfo => {
  const { hasFullAccess, visitorModeEnabled, isAdmin, isSuperAdmin } = user || {};
  const isVisitor = visitorModeEnabled || (!isAdmin && !hasFullAccess);
  const hasBookTime = isVisitor && !!bookTimeLink;
  const externalMenuItems = convertNavLinksToMenuItems(navLinks, appName);

  return {
    hasAiEngineManager,
    user,
    externalMenuItems,
    bookTimeLink,
    hasBookTime,
    hasHomePage,
    hasMyApp: hasBookTime || (hasFullAccess && !visitorModeEnabled),
    hasAdminMarketPlaceApps: isAdmin && !visitorModeEnabled && federationEnabled,
    hasAdminMenus: isAdmin && !visitorModeEnabled,
    hasSuperAdminMenus: isSuperAdmin && !visitorModeEnabled,
    hasVisitorMode: hasFullAccess,
    isPublicMode,
    isVisitorMode: visitorModeEnabled,
    url,
    hasMenu,
    environmentName,
    hasPlatformUsage,
    hasNotebook,
    hasSecureStore,
    cliDownloadEnabled,
    adminCenterEnabled,
    hasAuditTrail,
    driveEnabled,
  };
};

export type GetNavigationConfigParams = {
  userMenu: IContextualMenuItem[];
  env: EnvironmentAndMenu;
  cloudPlatformDiscovery?: CloudPlatformDiscoveryConfig;
  permissions?: Permissions;
  appStoreCategories?: NavigationElementConfig[];
  user: User;
  activeWorkspaceName?: string;
  bookTimeLink: string;
  isAuthenticated: boolean;
  hasAuthzPermission: (permission: { action: string; resource?: string }) => boolean;
  isLoadingPermissions?: boolean;
  mlopsMonitoringUrl?: string;
};

export const getNavigationConfig = (params: GetNavigationConfigParams): NavigationConfig => {
  const {
    env,
    userMenu = [],
    cloudPlatformDiscovery,
    permissions,
    appStoreCategories = [],
    user,
    activeWorkspaceName,
    bookTimeLink = 'https://h2o.ai',
    isAuthenticated,
    hasAuthzPermission,
    isLoadingPermissions,
    mlopsMonitoringUrl,
  } = params;

  // Build the navigation context for filtering
  const ctx: NavContext = {
    env,
    user,
    isAuthenticated,
    cloudPlatformDiscovery,
    permissions,
    activeWorkspaceName,
    hasAuthzPermission,
    isLoadingPermissions,
    mlopsMonitoringUrl,
  };

  // Handle external menu items from backend
  const externalMenuItems = env?.menu?.externalMenuItems || [];
  const externalMenuItemsObject = navArrayToObject(externalMenuItems);
  const restExternalMenuItems = Object.values(externalMenuItemsObject).filter((item) => !item.rendered);
  const userMenuObject = navArrayToObject<IContextualMenuItem>(userMenu);

  // Build main navigation with appStore categories
  const mainNavItems = filterNavItems(MAIN_NAV_ITEMS, ctx).map((item) => {
    if (item.key === AppStoreMenuName.appStore) {
      return {
        ...item,
        ...(appStoreCategories.length > 0
          ? {
              section: {
                top: [{ key: 'app-store-sub', name: 'Categories', elements: appStoreCategories }],
              },
            }
          : {}),
      };
    }
    return item;
  });

  // Build my navigation with external menu items
  const myNavItems = [
    ...filterNavItems(MY_NAV_ITEMS, ctx),
    ...getMenuElementsIfExist(externalMenuItemsObject, ['MyNotebookLab', 'Steam', 'MyModels'], true).map(
      (menuItem) => ({
        ...menuItem,
        name: menuItem.name.replace('My ', ''),
        more: {
          text: <Icon iconName="NavigateExternalInline" />,
        },
        image: (() => {
          switch (menuItem.name) {
            case 'My Notebook Lab':
              return <NavigationIcon src={NotebookIcon} />;
            case 'My Models':
              return <NavigationIcon src={MLOpsIcon} />;
            case 'Steam':
              return <NavigationIcon src={SteamIcon} />;
            default:
              return <Image src="/logo.svg" alt="Logo" style={{ width: 20, height: 20 }} />;
          }
        })(),
      })
    ),
  ];

  // Build operations navigation
  const operationsNavItems = filterNavItems(OPERATIONS_NAV_ITEMS, ctx);

  // Build AI for Documents section from external menu items
  const aiForDocsItems = getMenuElementsIfExist(
    externalMenuItemsObject,
    ['Enterpriseh2oGPTe', 'h2oGPT', 'DocumentAI', 'EvalStudio'],
    true
  ).map((menuItem) => ({
    ...menuItem,
    more: { text: <Icon iconName="NavigateExternalInline" /> },
    image: (() => {
      switch (menuItem.name) {
        case 'Enterprise h2oGPTe':
          return <NavigationIcon src={EnterpriseIcon} />;
        case 'Document AI':
          return <NavigationIcon src={DocAIIcon} />;
        case 'Eval Studio':
          return <NavigationIcon src={EvalIcon} />;
        default:
          return <Image src="/logo.svg" alt="Logo" style={{ width: 20, height: 20 }} />;
      }
    })(),
  }));

  // Build admin navigation
  const adminNavItems = filterNavItems(ADMIN_NAV_ITEMS, ctx);

  // Assemble top sections, filtering out empty ones
  const topSections = [
    { key: 'main-navigation', elements: mainNavItems },
    { key: 'my-navigation', name: 'My', elements: myNavItems },
    { key: 'operations', name: 'Operations', elements: operationsNavItems },
    { key: 'ai-for-documents', name: 'AI for Documents', elements: aiForDocsItems },
    ...(user.isAdmin || user.isSuperAdmin ? [{ key: 'admin', name: 'Admin', elements: adminNavItems }] : []),
    ...(restExternalMenuItems.length > 0
      ? [{ key: 'navElements-overflow', name: 'Links', elements: restExternalMenuItems }]
      : []),
  ].filter((section) => section.elements.length > 0);

  const navigationSectionConfig: NavigationSectionConfig = {
    top: topSections,
    bottom: [
      {
        key: 'footer-nav',
        elements: env?.menu?.hasBookTime
          ? [
              {
                key: AppStoreMenuName.collaborate,
                name: AppStoreMenuName.collaborate,
                dataTest: 'book-time--main-menu-item',
                to: bookTimeLink,
                isExternal: true,
              },
            ]
          : [],
      },
      {
        key: 'version',
        elements: [
          {
            key: 'version-title',
            text: 'H2O AI Cloud',
            more: { text: env.h2oCloudVersion },
          },
        ],
      },
    ],
  };

  const userMenuItems = getMenuElementsIfExist(userMenuObject, [
    'Documentation',
    'PlatformUsage',
    'VisitorMode',
    'CLI&APIAccess',
    'APIAccess',
    'About',
    'Logout',
  ]);

  return { navigationSectionConfig, userMenuItems, isLoading: false };
};

export const getFeatureStoreUserMenus = ({ hasMenu }: FeatureStoreMenuInfo = {}) => {
  if (!hasMenu) {
    return [];
  }
  // We don't have any user menus for Feature Store for now
  // const url = info?.url || '';
  const menus: IContextualMenuItem[] = [];
  return menus;
};

const navigateOrAddHistory = (path = '', history?: History, url = '') => {
  const link = `${url}${path}`;
  if (history && !url) {
    history.push(link);
  } else if (url) {
    navigate(link);
  }
};

export const getAppStoreUserMenus = (
  { hasMenu, url = '', hasPlatformUsage, isPublicMode, cliDownloadEnabled }: AppStoreMenuInfo,
  history?: History
) => {
  if (!hasMenu) return [];
  const menus: IContextualMenuItem[] = [];

  if (!isPublicMode && hasPlatformUsage) {
    menus.push({
      key: AppStoreMenuName.platformUsage,
      text: AppStoreMenuName.platformUsage,
      'data-test': 'platform-usage--user-menu-item',
      onClick: () => navigateOrAddHistory('/platform-usage', history, url),
    });
  }
  menus.push({
    key: AppStoreMenuName.documentation,
    text: AppStoreMenuName.documentation,
    onClick: () => navigate('https://docs.h2oai.com/haic-documentation/'),
    'data-test': 'doc--menu-item',
  });
  if (!isPublicMode) {
    menus.push({
      key: cliDownloadEnabled ? AppStoreMenuName.cliApiAccess : AppStoreMenuName.apiAccess,
      text: cliDownloadEnabled ? AppStoreMenuName.cliApiAccess : AppStoreMenuName.apiAccess,
      onClick: () => navigateOrAddHistory('/cli-and-api-access', history, url),
      'data-test': 'cli-and-api-access--menu-item',
    });
  }
  return menus;
};

export const getUserMenuItems = (
  appStore?: AppStoreMenuInfo,
  featureStore?: FeatureStoreMenuInfo,
  onLogout?: () => void,
  history?: History,
  onAbout?: () => void
) => {
  const menuItems: IContextualMenuItem[] = [];
  if (appStore) {
    menuItems.push(...getAppStoreUserMenus(appStore, history));
  }
  if (featureStore) {
    menuItems.push(...getFeatureStoreUserMenus(featureStore));
  }
  if (onAbout) {
    menuItems.push({
      key: AppStoreMenuName.about,
      text: AppStoreMenuName.about,
      onClick: onAbout,
      'data-test': 'about--user-menu-item',
    });
  }
  if (onLogout) {
    menuItems.push({
      key: AppStoreMenuName.logout,
      text: AppStoreMenuName.logout,
      onClick: onLogout,
      'data-test': 'logout--user-menu-item',
    });
  }

  return menuItems;
};
